import { useTranslation } from 'react-i18next';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { Banner, Button, Flex, Header, Spacer, spaces } from '@electricjs/arc';
import AddLicensesModal from '@/components/Licenses/AddLicensesModal';
import { NoWorkEmailBanner } from './NoWorkEmailBanner';
import {
  EmployeeStatusEnum,
  PackageNameEnum,
  RequestStatusEnum,
} from '@electricjs/core_entity-client';

import { CenteredSpinner } from '@common';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useUpdateRequestStatus } from '@/hooks/useUpdateRequestStatus';
import { AddEmployeeParams } from '@/pages/Onboardings/NewOnboarding';
import { useUpdateEmployeeMutation } from '@/redux/slices/employeeApiSlice';
import { useGetOrganizationPackagesQuery } from '@/redux/slices/packageApiSlice';
import { getFullName } from 'common/utils/getFullName';
import { FormActionsContainer } from './common';
import LicenseTile from './LicenseTileOption';
import { HiringStatus, OnboardingFormData, OnboardingStep } from './types';

export type LicenseSelectionProps = {
  canOnboard: boolean;
  addEmployee: (params: AddEmployeeParams) => void;
  handleSetInactive: () => void;
  existingEmployeeId?: string;
  hrisOnboardingRequestId?: string;
  isHRISActiveEmployeeOnboarding?: boolean;
};

const LicenseSelection = ({
  canOnboard,
  addEmployee,
  handleSetInactive,
  existingEmployeeId,
  hrisOnboardingRequestId = '',
  isHRISActiveEmployeeOnboarding = false,
}: LicenseSelectionProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { showErrorToast, openModal, closeModal } = useGlobalUI();
  const { selfServiceSeatAmendment } = useFlags();

  const handleAddLicenses = selfServiceSeatAmendment
    ? () => openModal(<AddLicensesModal onClose={closeModal} />)
    : undefined;
  const { control, setValue } = useFormContext<OnboardingFormData>();
  const { isSubmitting } = useFormState();
  const { space_2xl } = spaces;
  const [firstName, lastName, email, organizationPackageId, hiringStatus] =
    useWatch({
      control,
      name: [
        'demographics.firstName',
        'demographics.lastName',
        'employeeInformation.email',
        'organizationPackageId',
        'hiringStatus',
      ],
    });
  const organizationId = useGetOrganizationId();
  const fullName = getFullName(firstName, lastName);

  // Check if employee has work email for HRIS onboarding
  const isBlockedByMissingEmail = !!existingEmployeeId && !email;

  const [updateEmployee, { isLoading: isUpdatingEmployee }] =
    useUpdateEmployeeMutation();
  const { updateRequest, updateRequestLoading } = useUpdateRequestStatus({
    requestId: hrisOnboardingRequestId,
  });

  const { data: packagesData, isLoading: isLoadingPackages } =
    useGetOrganizationPackagesQuery(organizationId);
  const proPackage = packagesData?.find(
    pkg => pkg.package.name === PackageNameEnum.Pro
  );
  const lightPackage = packagesData?.find(
    pkg => pkg.package.name === PackageNameEnum.Lyte
  );
  const proLicenseCount = proPackage?.licenseCount;
  const lightLicenseCount = lightPackage?.licenseCount;
  const proAvailableLicenseCount = proPackage?.availableLicenseCount;
  const lightAvailableLicenseCount = lightPackage?.availableLicenseCount;

  const isNewHireWithOnboardPermission =
    canOnboard && hiringStatus === HiringStatus.New;
  const isHrisTask = hiringStatus === HiringStatus.Hris;
  const nextButtonLabel =
    isNewHireWithOnboardPermission || isHrisTask
      ? t('onboardings.licenseSelection.nextButton')
      : t('onboardings.licenseSelection.setEmployeeActiveButton');
  const disableNextButton = !organizationPackageId || isBlockedByMissingEmail;

  const handleSelectLicense = (organizationPackageId: string) => {
    setValue('organizationPackageId', organizationPackageId, {
      shouldValidate: true,
    });
  };

  const handlePrevious = () => {
    setValue('currentStep', OnboardingStep.EmployeeInformation, {
      shouldValidate: true,
    });
  };

  const addNewEmployee = async () => {
    addEmployee({
      onSuccess: (employeeId: string) => {
        if (employeeId) {
          navigate(`/employees/${employeeId}`);
        }
      },
      status: EmployeeStatusEnum.Active,
      organizationPackageId,
    });
  };

  const updateHrisEmployee = async (existingEmployeeId: string | undefined) => {
    if (!existingEmployeeId) {
      showErrorToast({
        id: 'license-selection-error-toast',
        message: t('onboardings.licenseSelection.errorToast'),
        stack: true,
      });
      return;
    }

    try {
      await updateEmployee({
        id: existingEmployeeId,
        status: EmployeeStatusEnum.Active,
        organizationPackageId: organizationPackageId,
        organizationId,
      }).unwrap();

      // If it exists and the user doesn't have permission to onboard, mark HRIS onboarding request as completed
      if (hrisOnboardingRequestId && !canOnboard) {
        await updateRequest({
          newStatus: RequestStatusEnum.Completed,
        });
      }

      navigate(`/employees/${existingEmployeeId}`);
    } catch (error) {
      console.error('Failed to update employee:', error);
      showErrorToast({
        id: 'license-selection-error-toast',
        message: t('onboardings.licenseSelection.failedToUpdateEmployee'),
        stack: true,
      });
      throw error; // Re-throw to allow caller to handle if needed
    }
  };

  const handleNext = async () => {
    // When the current user has permission to onboard:
    // - If the employee is a new hire or from HRIS, proceed to the Onboarding step
    // - Otherwise, add the employee directly and navigate to their profile
    // When the current user does not have permission to onboard:
    // - If the employee is a new hire or existing, add the employee directly and navigate to their profile
    // - Otherwise (HRIS employee), update their status and package and navigate to their profile
    if (hiringStatus === HiringStatus.Hris) {
      setValue(
        'currentStep',
        isHRISActiveEmployeeOnboarding
          ? OnboardingStep.Applications
          : OnboardingStep.Onboarding,
        { shouldValidate: true }
      );
      return;
    }

    if (canOnboard) {
      if (hiringStatus === HiringStatus.New) {
        setValue('currentStep', OnboardingStep.Onboarding, {
          shouldValidate: true,
        });
        return;
      }
      await addNewEmployee();
      return;
    }

    if (
      hiringStatus === HiringStatus.New ||
      hiringStatus === HiringStatus.Existing
    ) {
      await addNewEmployee();
      return;
    }

    // For HRIS employees, update their status and package
    await updateHrisEmployee(existingEmployeeId);
  };

  const isUpdating = isSubmitting || isUpdatingEmployee || updateRequestLoading;

  if (isLoadingPackages) {
    return <CenteredSpinner py="4.3rem" />;
  }

  if (isBlockedByMissingEmail) {
    return <NoWorkEmailBanner employeeName={fullName} />;
  }

  if (!proPackage || !lightPackage) {
    return (
      <Banner
        id="license-selection-error-banner"
        isFlat
        intent="error"
        title={t('onboardings.licenseSelection.errorTitle')}
        message={t('onboardings.licenseSelection.errorMessage')}
        cta={t('onboardings.licenseSelection.refreshButton')}
        onClickCta={() => window.location.reload()}
      />
    );
  }

  return (
    <>
      <Header
        title={t('onboardings.licenseSelection.title')}
        subtitle={t('onboardings.licenseSelection.subtitle', { fullName })}
      />
      <Spacer y={space_2xl} />
      <Flex gap="2rem" flexWrap="wrap" justifyContent="center">
        <LicenseTile
          packageId={proPackage?.id}
          packageSlug={PackageNameEnum.Pro}
          selected={organizationPackageId === proPackage?.id}
          licenseCount={proLicenseCount ?? null}
          licensesAvailable={proAvailableLicenseCount ?? null}
          onSelect={handleSelectLicense}
          hideLicenseCount={!proLicenseCount && !lightLicenseCount}
          disabled={!!proLicenseCount && !proAvailableLicenseCount}
          onAddLicenses={handleAddLicenses}
        />
        <LicenseTile
          packageId={lightPackage?.id}
          packageSlug={PackageNameEnum.Lyte}
          selected={organizationPackageId === lightPackage?.id}
          licenseCount={lightLicenseCount ?? null}
          licensesAvailable={lightAvailableLicenseCount ?? null}
          onSelect={handleSelectLicense}
          hideLicenseCount={!proLicenseCount && !lightLicenseCount}
          disabled={!!lightLicenseCount && !lightAvailableLicenseCount}
          onAddLicenses={handleAddLicenses}
        />
      </Flex>
      <FormActionsContainer>
        <Button
          id="license-previous-button"
          variant="outline"
          onClick={handlePrevious}
          disabled={isUpdating}>
          {t('onboardings.licenseSelection.previousButton')}
        </Button>
        <Flex gap="2rem">
          <Button
            id="onboarding-no-license-needed-button"
            onClick={handleSetInactive}
            disabled={isUpdating}>
            {t('onboardings.licenseSelection.noLicenseNeededButton')}
          </Button>
          <Button
            id="onboarding-next-button"
            onClick={handleNext}
            disabled={disableNextButton}
            loading={isUpdating}>
            {nextButtonLabel}
          </Button>
        </Flex>
      </FormActionsContainer>
    </>
  );
};

export default LicenseSelection;
